import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

/**
 * Admin: Item Discounts. Pick a restaurant, then apply a discount to any specific
 * item from TWO independent slots — Restaurant and Platform — which COMBINE ADDITIVELY
 * into one effective price the customer sees and pays. (Both slots are restaurant-
 * absorbed in settlement.) Set value 0 / Remove to clear a slot.
 */
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
// Mirror backend utils normalization so we can fall back to name-matching Zomato discounts.
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Additive combine — mirrors backend utils/menuDiscount.combinedDiscountedPaisa.
function effPrice(price, ...slots) {
  let pct = 0, flat = 0;
  for (const d of slots) {
    if (!d || !(Number(d.value) > 0)) continue;
    if (d.type === 'flat') flat += Number(d.value); else pct += Number(d.value);
  }
  pct = Math.max(0, Math.min(100, pct));
  return Math.max(0, Math.round(price * (100 - pct)) / 100 - flat);
}

const box = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 };
const th = { textAlign: 'left', padding: '10px 10px', fontWeight: 600, borderBottom: '2px solid #e5e7eb', fontSize: 12.5, color: '#374151', whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 13, verticalAlign: 'middle' };
const inp = { padding: 6, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 };
const btn = (bg, fg) => ({ padding: '5px 10px', border: 'none', borderRadius: 8, background: bg, color: fg, cursor: 'pointer', fontSize: 12, fontWeight: 700 });
const badge = (bg, fg) => ({ background: bg, color: fg, padding: '2px 8px', borderRadius: 12, fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap' });

// A single discount-slot editor (type + value + Apply/Remove) for one item.
function SlotEditor({ item, draft, slotLabel, onChange, onApply, current }) {
  const active = current?.active && Number(current?.value) > 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <select value={draft.type} onChange={(e) => onChange('type', e.target.value)} style={inp}>
        <option value="percent">%</option>
        <option value="flat">₹</option>
      </select>
      <input type="number" min="0" max={draft.type === 'percent' ? 100 : undefined} step={draft.type === 'percent' ? 1 : 0.5}
        value={draft.value} onChange={(e) => onChange('value', e.target.value)} style={{ ...inp, width: 66 }} />
      <button style={btn('#111827', '#fff')} onClick={() => onApply(true)}>Apply</button>
      {active && <button style={btn('#fee2e2', '#991b1b')} onClick={() => onApply(false)}>✕</button>}
      {active && <span style={badge('#dcfce7', '#166534')}>{current.type === 'flat' ? `₹${current.value}` : `${current.value}%`}</span>}
    </div>
  );
}

export default function ItemDiscounts() {
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState('');
  const [rfilter, setRfilter] = useState('');
  const [items, setItems] = useState([]);
  const [ifilter, setIfilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [draft, setDraft] = useState({}); // itemId -> { rest:{type,value}, plat:{type,value} }
  const [zdisc, setZdisc] = useState({ byId: {}, byName: {}, count: 0 }); // Zomato item-level discounts for the picked restaurant
  const [onlyZ, setOnlyZ] = useState(false); // filter to only items Zomato is discounting

  useEffect(() => {
    adminAPI.getAllRestaurants().then((r) => {
      setRestaurants(r.data?.data?.restaurants || r.data?.restaurants || []);
    }).catch(() => setMsg('Failed to load restaurants'));
  }, []);

  const initDraft = (list) => {
    const d = {};
    list.forEach((it) => {
      d[it._id] = {
        rest: { type: it.discount?.type || 'percent', value: it.discount?.active ? (it.discount?.value ?? 0) : 0 },
        plat: { type: it.platformDiscount?.type || 'percent', value: it.platformDiscount?.active ? (it.platformDiscount?.value ?? 0) : 0 },
      };
    });
    setDraft(d);
  };

  const loadItems = useCallback(async (rid) => {
    if (!rid) { setItems([]); setZdisc({ byId: {}, byName: {}, count: 0 }); return; }
    setLoading(true); setMsg(''); setZdisc({ byId: {}, byName: {}, count: 0 });
    try {
      const r = await adminAPI.getRestaurantMenu(rid);
      const list = r.data?.data?.menuItems || r.data?.data?.menu || r.data?.menuItems || [];
      setItems(list);
      initDraft(list);
    } catch (e) { setMsg(e.response?.data?.message || 'Failed to load items'); }
    finally { setLoading(false); }
    // Separately fetch which of these items Zomato is currently discounting (highlight only;
    // nothing is auto-applied — the owner decides). Never blocks the menu load.
    try {
      const zr = await adminAPI.ciZomatoItemDiscounts(rid);
      const arr = zr.data?.data?.items || [];
      const byId = {}, byName = {};
      arr.forEach((z) => { if (z.ourItemId) byId[z.ourItemId] = z; byName[norm(z.ourName)] = z; });
      setZdisc({ byId, byName, count: arr.length });
    } catch { /* highlight is best-effort */ }
  }, []);

  const onPickRestaurant = (id) => { setRestaurantId(id); setIfilter(''); setOnlyZ(false); loadItems(id); };
  const zFor = (it) => zdisc.byId[it._id] || zdisc.byName[norm(it.name)] || null;

  const setD = (id, which, k, v) => setDraft((p) => ({
    ...p, [id]: { ...(p[id] || { rest: { type: 'percent', value: 0 }, plat: { type: 'percent', value: 0 } }), [which]: { ...(p[id]?.[which] || { type: 'percent', value: 0 }), [k]: v } },
  }));

  const apply = async (item, which, active) => {
    const slot = which === 'plat' ? 'platform' : 'restaurant';
    const d = draft[item._id]?.[which] || { type: 'percent', value: 0 };
    const value = active ? Math.max(0, Number(d.value) || 0) : 0;
    setMsg('');
    try {
      const res = await adminAPI.setMenuItemDiscount(restaurantId, item._id, { slot, active, type: d.type, value });
      setMsg(res.data?.message || 'Saved');
      const updated = res.data?.data?.menuItem;
      const field = which === 'plat' ? 'platformDiscount' : 'discount';
      setItems((prev) => prev.map((it) => it._id === item._id ? { ...it, [field]: updated?.[field] || { active, type: d.type, value } } : it));
    } catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
  };

  const rlist = restaurants.filter((r) => !rfilter || (r.name || '').toLowerCase().includes(rfilter.toLowerCase()));
  const ilist = items.filter((it) =>
    (!ifilter || (it.name || '').toLowerCase().includes(ifilter.toLowerCase()) || (it.category || '').toLowerCase().includes(ifilter.toLowerCase()))
    && (!onlyZ || !!zFor(it)));

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 16px 60px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>🏷️ Item Discounts</h1>
        <p style={{ color: '#6b7280', marginBottom: 18 }}>
          Pick a restaurant, then discount any specific item from two slots that combine additively into one price the
          customer sees &amp; pays. <b>🏪 Restaurant</b> = the restaurant funds it (paid less). <b>🅿️ Platform</b> = FoodWinc
          funds it (the restaurant is still paid its full price for that portion). Set 0 / ✕ to clear a slot.
        </p>

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
          <label style={{ fontSize: 13 }}>Restaurant<br />
            <select value={restaurantId} onChange={(e) => onPickRestaurant(e.target.value)} style={{ ...inp, minWidth: 320 }}>
              <option value="">— Select a restaurant —</option>
              {rlist.map((r) => <option key={r._id} value={r._id}>{r.name}{r.address?.city ? ` · ${r.address.city}` : ''}</option>)}
            </select>
          </label>
          <input placeholder="Filter restaurants…" value={rfilter} onChange={(e) => setRfilter(e.target.value)} style={{ ...inp, height: 32 }} />
          {restaurantId && <input placeholder="Filter items…" value={ifilter} onChange={(e) => setIfilter(e.target.value)} style={{ ...inp, height: 32 }} />}
          {restaurantId && zdisc.count > 0 && (
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyZ} onChange={(e) => setOnlyZ(e.target.checked)} />
              Only Zomato-discounted
            </label>
          )}
        </div>

        {restaurantId && !loading && (
          zdisc.count > 0 ? (
            <div style={{ marginBottom: 14, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e' }}>
              🔥 Zomato is currently running an item discount on <b>{zdisc.count}</b> of this restaurant's matched item{zdisc.count === 1 ? '' : 's'} — highlighted in amber below with Zomato's price. These are <b>not</b> applied automatically: decide what (if anything) to discount yourself.
            </div>
          ) : (
            <div style={{ marginBottom: 14, fontSize: 12.5, color: '#9ca3af' }}>No Zomato item-level discounts detected for this restaurant right now.</div>
          )
        )}

        {msg && <div style={{ marginBottom: 12, fontSize: 13, color: msg.toLowerCase().includes('fail') ? '#b91c1c' : '#166534' }}>{msg}</div>}
        {loading && <div style={{ color: '#6b7280' }}>Loading items…</div>}

        {!loading && restaurantId && (
          <div style={{ ...box, overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1040 }}>
              <thead><tr>
                <th style={th}>Item</th><th style={th}>Price</th>
                <th style={th}>🏪 Restaurant discount<div style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>restaurant pays</div></th><th style={th}>🅿️ Platform discount<div style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>FoodWinc pays</div></th>
                <th style={th}>Combined price</th>
              </tr></thead>
              <tbody>
                {ilist.map((it) => {
                  const dr = draft[it._id] || { rest: { type: 'percent', value: 0 }, plat: { type: 'percent', value: 0 } };
                  const preview = effPrice(it.price, dr.rest, dr.plat);
                  const off = it.price > 0 ? Math.round((1 - preview / it.price) * 100) : 0;
                  const z = zFor(it);
                  return (
                    <tr key={it._id} style={z ? { background: '#fffbeb' } : undefined}>
                      <td style={td}>{it.name}<div style={{ fontSize: 11, color: '#9ca3af' }}>{it.category}</div>
                        {z && (
                          <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={badge('#fef3c7', '#92400e')} title={`Matched Zomato item: ${z.zomatoName}`}>🔥 Zomato {inr(z.zomatoListPrice)} → {inr(z.zomatoDiscountedPrice)} (−{z.pct}%)</span>
                          </div>
                        )}
                      </td>
                      <td style={td}>{inr(it.price)}</td>
                      <td style={td}>
                        <SlotEditor item={it} draft={dr.rest} current={it.discount}
                          onChange={(k, v) => setD(it._id, 'rest', k, v)} onApply={(a) => apply(it, 'rest', a)} />
                      </td>
                      <td style={td}>
                        <SlotEditor item={it} draft={dr.plat} current={it.platformDiscount}
                          onChange={(k, v) => setD(it._id, 'plat', k, v)} onApply={(a) => apply(it, 'plat', a)} />
                      </td>
                      <td style={{ ...td, fontWeight: 700, color: preview < it.price ? '#166534' : '#374151', whiteSpace: 'nowrap' }}>
                        {inr(preview)}{preview < it.price && <span style={{ ...badge('#dcfce7', '#166534'), marginLeft: 6 }}>{off}% OFF</span>}
                      </td>
                    </tr>
                  );
                })}
                {!ilist.length && <tr><td style={td} colSpan={5}>No items{ifilter ? ' match the filter' : ''}.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {!restaurantId && !loading && <div style={{ color: '#9ca3af' }}>Select a restaurant to see its menu items.</div>}
      </div>
    </>
  );
}
